import { Directive, HostListener, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, _ } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { firstValueFrom } from 'rxjs';
import { HistoryEntry, StoreHistoryService } from 'src/app/services/store-history/store-history.service';
import { StoreHistoryTelemetryService } from 'src/app/services/store-history/store-history-telemetry.service';
import type {
  UndoConfirmData,
  UndoConfirmResult,
  UndoSavedConfirmDialogComponent,
} from './undo-confirm/undo-confirm.component';

type ShortcutAction = 'undo' | 'redo';

/** Input types with no text to undo — a merchant tabbed onto a checkbox still gets the shortcut. */
const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

const shortcutAction = (event: KeyboardEvent): ShortcutAction | undefined => {
  if (!event.metaKey && !event.ctrlKey) return undefined;

  const key = event.key.toLowerCase();

  if (key === 'z') return event.shiftKey ? 'redo' : 'undo';
  if (key === 'y' && !event.shiftKey) return 'redo';

  return undefined;
};

/**
 * Whether the browser already means something by this keystroke.
 *
 * Text fields have native undo, and taking it away is a regression the merchant feels on the first
 * mistyped size label. This is the check the abandoned implementation in `chart-info` did not have,
 * and the reason that one was never turned on.
 */
const isTextEntry = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type);

  // CKEditor moves focus onto descendants of its editable region that are not themselves
  // contenteditable — widgets, the balloon toolbar — and every one of them is inside `.ck`.
  return target.closest('.ck') !== null;
};

/**
 * `Cmd/Ctrl+Z` and its redo counterparts, bound once for the whole authenticated app.
 *
 * The shortcut used to be impossible to bind honestly: `Cmd+Z` had two candidate meanings a click
 * apart — the chart editor's unsaved-draft stack, and a persisted store-wide undo. One cursor in
 * `StoreHistoryService` removes the ambiguity. The shortcut always reverses the merchant's most
 * recent action, and which tier that action lives in is an implementation detail with exactly one
 * visible consequence: crossing into the saved tier is a server write, so the first time a session
 * does it, it asks.
 *
 * Bound on the authenticated layout, which is created once when auth resolves and outlives every
 * navigation — so "don't ask again this session" really is the session.
 */
@Directive({ selector: '[appHistoryShortcut]', standalone: true })
export class HistoryShortcutDirective {
  private readonly storeHistory = inject(StoreHistoryService);
  private readonly dialog = inject(MatDialog);
  private readonly snackbar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly telemetry = inject(StoreHistoryTelemetryService);

  private confirmSuppressed = false;
  /** Guards the server round-trip: a held-down `Cmd+Z` must not queue six saved undos. */
  private pending = false;

  @HostListener('document:keydown', ['$event'])
  async onKeydown(event: KeyboardEvent): Promise<void> {
    const action = shortcutAction(event);

    if (!action) return;
    if (isTextEntry(event.target)) return;
    // A dialog owns the keyboard while it is open — including this card's own confirm.
    if (this.dialog.openDialogs.length) return;
    if (this.pending) return;

    if (action === 'redo') return this.onRedo(event);

    return this.onUndo(event);
  }

  private async onUndo(event: KeyboardEvent): Promise<void> {
    if (!this.storeHistory.canUndo()) return;

    const next = this.storeHistory.nextUndo();

    // Nothing nameable to reverse. Reachable only if the saved chain and its flags disagree, and
    // writing to the store on a keystroke that cannot say what it is reversing is not acceptable.
    if (!next) return;

    event.preventDefault();

    if (next.tier === 'draft') {
      await this.run(() => this.storeHistory.undo());
      this.telemetry.shortcutCompleted('draft', false);

      return;
    }

    if (!this.confirmSuppressed) return this.confirmThenUndo(next);

    await this.run(() => this.storeHistory.undo());
    // `confirmed: false` because this session already waved the confirm off — the pair of these
    // against `shortcut.dismissed` is what says whether the fall-through surprises people.
    this.telemetry.shortcutCompleted('saved', false);

    this.offerRedo(next);
  }

  private async onRedo(event: KeyboardEvent): Promise<void> {
    if (!this.storeHistory.canRedo()) return;

    event.preventDefault();

    // Redo never confirms: it re-applies something the merchant reversed moments ago, and the
    // reversal is what they are being protected from doing by accident.
    await this.run(() => this.storeHistory.redo());
  }

  private async confirmThenUndo(entry: HistoryEntry): Promise<void> {
    // Lazily loaded, as every other dialog in this app is: most sessions never cross the tier
    // boundary, and the layout this directive is bound to loads on every page.
    const component = await import('./undo-confirm/undo-confirm.component').then(
      (module) => module.UndoSavedConfirmDialogComponent,
    );

    const result = await firstValueFrom(
      this.dialog
        .open<UndoSavedConfirmDialogComponent, UndoConfirmData, UndoConfirmResult>(component, {
          data: { entry },
          width: '420px',
        })
        .afterClosed(),
    );

    if (!result) {
      this.telemetry.shortcutDismissed();

      return;
    }

    this.confirmSuppressed = result.dontAskAgain;

    await this.run(() => this.storeHistory.undo());
    this.telemetry.shortcutCompleted('saved', true);
  }

  private async run(action: () => Promise<void>): Promise<void> {
    this.pending = true;

    try {
      await action();
    } catch (error) {
      captureException(error);
      this.snackbar.open(
        (error as Error)?.message ?? this.translate.instant(_('error.error-occured')),
        this.translate.instant(_('common.close')),
        { panelClass: 'snackbar-error' },
      );
    } finally {
      this.pending = false;
    }
  }

  /** The escape hatch that replaces the confirm once the merchant has waved it off. */
  private offerRedo(entry: HistoryEntry): void {
    this.snackbar
      .open(
        this.translate.instant(_('store-history.undid-x'), {
          x: this.translate.instant(entry.labelKey, entry.labelParams),
        }),
        this.translate.instant(_('common.undo')),
        { duration: 8000 },
      )
      .onAction()
      .subscribe(() => this.run(() => this.storeHistory.redo()));
  }
}
